import type { ImgHTMLAttributes } from "react";
import { cn } from "~/shared/lib/utils";

type ResponsiveImageVariant = {
  width: number;
  url: string;
};

type NativeImgProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "alt" | "loading" | "decoding"
>;

type ResponsiveImageProps = NativeImgProps & {
  src: string;
  alt: string;
  variants?: ResponsiveImageVariant[];
  sizes?: string;
  loading?: "lazy" | "eager";
  decoding?: "async" | "sync" | "auto";
  className?: string;
};

function buildSrcSet(variants: ResponsiveImageVariant[] | undefined) {
  if (!variants || variants.length === 0) return undefined;
  const seen = new Set<number>();
  const parts: string[] = [];
  for (const v of variants) {
    if (seen.has(v.width)) continue;
    seen.add(v.width);
    parts.push(`${v.url} ${v.width}w`);
  }
  return parts.length > 0 ? parts.join(", ") : undefined;
}

export function ResponsiveImage({
  src,
  alt,
  variants,
  sizes,
  loading = "lazy",
  decoding = "async",
  className,
  ...rest
}: ResponsiveImageProps) {
  const srcSet = buildSrcSet(variants);

  return (
    <img
      {...rest}
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={loading}
      decoding={decoding}
      className={cn(className)}
    />
  );
}
